import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {when} from 'lit/directives/when.js';
import {repeat} from 'lit/directives/repeat.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {msg} from '@lit/localize';
import {ConfigurationInterpreter} from '@viamedici-spc/configurator-ts';

import store from '../../../store';
import {conditional} from '../../../util/conditional';
import {getProductData} from '../../../util/getProductData';
import {filterAndJoin} from '../../../util/filterAndJoin';
import {pimText, pimTextDebug} from '../../../util/values';
import {
	selectDimensionHeight,
	selectDimensionWidth,
	selectDimensionDepth,
	selectDimensionDiameter,
	selectWeight,
	selectWindsurfacearea,
	selectSpcConfiguration,
	selectSpcAttributes,
	selectProductData,
	selectSystemLumen,
	selectDimmLevel,
	selectCoefficients,
	selectHasCoefficients
} from '../../../selectors';
import {regionCheck} from '../../../util/regionCheck';
import {calculateCurrent, calculatePower} from '../../../util/dimLevel';
import {attributeIdToString, stringToAttributeId} from '../../../components/spc/util/spcUtils';

@customElement('olivio-specsheet-general-data')
export class OlivioSpecsheetGeneralData extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				margin: 0;
				display: flex;
				flex-direction: column;
				container-type: inline-size;
			}
			.subheadline {
				font: var(--font-specsheet-print-small-text);
				font-size: 2cqw;
				margin-top: 5cqw;
				color: #939393;
			}
		`]
	;

	constructor() {
		super();
		this.productData = null;
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.state = state;
		this.status = state.status;
		this.debugShowAll = state.status.debugShowAll;
		this.lang = state.status.lang;
		this.conditionalVariantValues = state.productData.conditionalVariantValues;

		this.productData = selectProductData(this.state);
		this.spcConfiguration = selectSpcConfiguration(state);
	}

	getValueFromProductData(featureId, featureValueId) {
		const pd = getProductData(featureId, this.productData);
		return pd?.values?.find(val => val.valueId === featureValueId);
	}

	t(featureId, context, value, prefix, suffix) {
		return this.debug ?
			pimTextDebug(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix) :
			pimText(this.lang, featureId, context, this.spcConfiguration, this.state.productData.translations, value, prefix, suffix);
	}

	c(id) {
		return conditional(this.spcConfiguration, this.conditionalVariantValues, id);
	}

	render() {
		const s = this.state;
		const lightEngineDescription = this.t(this.c('DescriptiveAttributeReference.LE_LightEngineDescription'), 'DV');
		const opticalVersion = this.t('LE_OpticalVersion_1', 'FV.S');
		const reflectorColour = this.t('LE_ReflectorColour_1', 'FV.S');
		const isLightEngineDescriptionPresent = lightEngineDescription || opticalVersion || reflectorColour;

		const controlNoWire = this.t('EB_ControlNoWire_1', 'FV.S');
		const controlWire = this.t('EB_ControlWire_1', 'FV.S');
		const isControlPresent = controlNoWire || controlWire;

		const dimensionsLabel = this.t('TE_Dimensions', 'D');
		const dimensionDiameter = selectDimensionDiameter(s);
		const dimensionDepth = selectDimensionDepth(s);
		const dimensionWidth = selectDimensionWidth(s);
		const dimensionHeight = selectDimensionHeight(s);
		const isDimensionsPresent = dimensionsLabel || dimensionDepth || dimensionWidth || dimensionHeight;

		const applicableStandards = filterAndJoin([
			this.t(this.c('DescriptiveAttributeReference.LU_IK'), 'DV.S'),
			this.t(this.c('DescriptiveAttributeReference.LU_IP'), 'DV.S'),
			this.t('EB_ElectricalProtectionClass_1', 'FV.S'),
			this.t(this.c('DescriptiveAttributeReference.LU_ENEC_CertificateDescription'), 'DV.S'),
			this.t(this.c('DescriptiveAttributeReference.LU_CE'), 'DV.S')
		], ', ');

		const descAtt = this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV.S');
		const wattageMax = this.c('REDTAB.RETURN_EB_Wattage_Max');

		const lumenAttribute = ConfigurationInterpreter.getChoiceAttribute(this.spcConfiguration, stringToAttributeId('root::Heads::Head::Type::Luminaire::LightEngine::LE_DimmingLevel'));
		const lumenValues = Array.from(lumenAttribute.values.values()).filter(v => v.possibleDecisionStates.includes('Included'));
		const maxLumenChoiceValue = lumenValues.sort((a, b) => parseInt(a.id, 10) - parseInt(b.id, 10)).pop();
		const maxLumenValue = maxLumenChoiceValue ? parseInt(maxLumenChoiceValue.id, 10) : 0;
		const maxLumenPerWatt = Math.round(
			maxLumenValue /
			parseInt(this.c('REDTAB.RETURN_EB_Wattage_Max'), 10) * 100
		) / 100;

		const dimmedLumenPerWatt = Math.round(
			parseInt(selectDimmLevel(s), 10) /
			calculatePower(selectDimmLevel(s), selectCoefficients(s).formular2.coefficients) * 100
		) / 100;

		return html`
			<!-- CRI -->
			<shared-specsheet-feature>
				<trans-label slot="label" key="LE_CRI" context="D"></trans-label>
				<span slot="value" class="value">
					${filterAndJoin([
						this.t('LE_CRI', 'D', null, '', ' ') + this.t(this.c('DescriptiveAttributeReference.LE_CRI'), 'DV')
					], ', ')}
				</span>
			</shared-specsheet-feature>

			<shared-specsheet-feature>
				<trans-label slot="label" key="LU_ULOR" context="D"></trans-label>
				<span slot="value">${this.c('REDTAB.RETURN_LU_ULOR')} %</span>
			</shared-specsheet-feature>

			<!-- Material -->
			${when(this.t('HS_HousingDescription', 'D.L') !== '', () => html`
				<shared-specsheet-feature>
					<trans-label slot="label" key="LU_LuminaireMaterial" context="D"></trans-label>
					<span slot="value">
						${filterAndJoin([
							this.t('LU_LuminaireMaterial.die-cast_aluminium', 'DV.L', null, '', ''),
							this.t(this.c('DescriptiveAttributeReference.LE_LightEngineCover'), 'DV.L', null, '', '')
						], ', ')}
					</span>
				</shared-specsheet-feature>
			`)}

			<!-- Technische Region -->
			<shared-specsheet-feature>
				<trans-label slot="label" key="LU_TechnicalRegion" context="D"></trans-label>
				<trans-label slot="value" class="value" key="LU_TechnicalRegion.220-240V 50/60 Hz" context="DV"></trans-label>
			</shared-specsheet-feature>

			<!-- Umgebungstemperatur -->
			<!--
			<shared-specsheet-feature>
				<trans-label slot="label" key="LU_AmbientTemperature" context="D"></trans-label>
				<span slot="value">${this.t(this.c('DescriptiveAttributeReference.LU_AmbientTemperature'), 'DV.S')}</span>
			</shared-specsheet-feature>
			-->

			<!-- Betriebsgerät -->
			<shared-specsheet-feature>
				<trans-label slot="label" key="EB_EBlockOperatingGearDescription" context="D"></trans-label>
				<trans-label slot="value" class="value" key="EB_EBlockOperatingGearDescription.ElectronicConverter" context="DV.S"></trans-label>
			</shared-specsheet-feature>

			<!-- Überspannungsschutz -->
			<shared-specsheet-feature>
				<trans-label slot="label" key="LU_SurgeProtection" context="D"></trans-label>
				<trans-label slot="value" key="LU_SurgeProtection.10kV" context="DV.S"></trans-label>
			</shared-specsheet-feature>

			<!-- Anschlussleitung -->
			<shared-specsheet-feature>
				<trans-label slot="label" key="HS_HousingPowerConnection" context="F"></trans-label>
				<span slot="value">${msg('connected supply line')}</span>
			</shared-specsheet-feature>

			<shared-specsheet-feature condition="${applicableStandards.trim()}">
				<trans-label slot="label" key="LU_ApplicableStandards" context="D"></trans-label>
				<span slot="value" class="value">${applicableStandards}</span>
			</shared-specsheet-feature>

			<!-- Wartung -->
			${when(this.t('LU_Maintanance', 'D.S') !== '', () => html`
				<shared-specsheet-feature>
					<trans-label slot="label" key="LU_Maintanance" context="D"></trans-label>
					<trans-label slot="value" key="LU_Maintanance" context="D.S"></trans-label>
				</shared-specsheet-feature>
			`)}

			<!-- Garantie -->
			${when(this.t('LU_Warranty', 'D.L') !== '', () => html`
				<shared-specsheet-feature>
					<trans-label slot="label" key="LU_Warranty" context="D"></trans-label>
					<trans-label slot="value" key="LU_Warranty" context="D.L"></trans-label>
				</shared-specsheet-feature>
			`)}


		`;
	}
}
